import * as THREE from 'three';

interface Chunk {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
  life: number;
  maxLife: number;
  floorY: number;
}

interface FallingProp {
  mesh: THREE.Object3D;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
  restY: number;
  settled: boolean;
}

const _chunkGeo = new THREE.BoxGeometry(1, 1, 1);
const _away = new THREE.Vector3();
const POOL_SIZE = 64;

/** Tumbling wood / concrete chunks for blasted doors and walls. */
export class BreakFx {
  private readonly pool: Chunk[] = [];
  private readonly live: Chunk[] = [];
  private readonly falling: FallingProp[] = [];

  constructor(private readonly scene: THREE.Object3D) {
    for (let i = 0; i < POOL_SIZE; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 1,
        fog: true,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(_chunkGeo, mat);
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.frustumCulled = true;
      mesh.visible = false;
      mesh.userData.ignoreHit = true;
      scene.add(mesh);
      this.pool.push({
        mesh,
        vel: new THREE.Vector3(),
        spin: new THREE.Vector3(),
        life: 0,
        maxLife: 1,
        floorY: 0,
      });
    }
  }

  spawnChunks(
    center: THREE.Vector3,
    size: THREE.Vector3,
    color: number,
    count = 12,
    kind: 'rubble' | 'plank' = 'rubble',
    floorY = 0.04,
  ): void {
    const span = Math.max(0.15, Math.min(size.x, size.y, size.z) * 0.45);
    const rest = floorY + 0.04;
    for (let i = 0; i < count; i++) {
      const c = this.pool.pop();
      if (!c) break;
      const plank = kind === 'plank';
      const sx = plank
        ? 0.14 + Math.random() * Math.min(0.32, size.x * 0.35 + 0.06)
        : 0.07 + Math.random() * Math.min(0.2, size.x * 0.22 + 0.04);
      const sy = plank
        ? 0.03 + Math.random() * 0.07
        : 0.06 + Math.random() * Math.min(0.18, size.y * 0.18 + 0.04);
      const sz = plank
        ? 0.05 + Math.random() * 0.1
        : 0.05 + Math.random() * Math.min(0.16, span);
      const mat = c.mesh.material as THREE.MeshBasicMaterial;
      mat.color.setHex(color);
      mat.opacity = 1;
      c.mesh.scale.set(sx, sy, sz);
      c.mesh.position.set(
        center.x + (Math.random() - 0.5) * size.x * 0.7,
        center.y + (Math.random() - 0.5) * size.y * 0.7,
        center.z + (Math.random() - 0.5) * size.z * 0.7,
      );
      c.mesh.rotation.set(Math.random() * 6, Math.random() * 6, Math.random() * 6);
      c.mesh.visible = true;
      c.vel.set((Math.random() - 0.5) * 7, 2.2 + Math.random() * 5, (Math.random() - 0.5) * 7);
      _away.copy(c.mesh.position).sub(center);
      if (_away.lengthSq() > 1e-5) c.vel.addScaledVector(_away.normalize(), 2.4);
      c.spin.set((Math.random() - 0.5) * 14, (Math.random() - 0.5) * 14, (Math.random() - 0.5) * 16);
      c.life = 1.2 + Math.random() * 0.7;
      c.maxLife = c.life;
      c.floorY = rest;
      this.live.push(c);
    }
  }

  /** Detach a crate-top paper / letter and let it flutter to the floor. */
  dropProp(obj: THREE.Object3D, floorY: number): void {
    if (obj.userData.dropped) return;
    obj.userData.dropped = true;
    obj.userData.ignoreHit = true;
    obj.updateWorldMatrix(true, false);
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    obj.matrixWorld.decompose(pos, quat, scl);
    obj.removeFromParent();
    this.scene.add(obj);
    obj.position.copy(pos);
    obj.quaternion.copy(quat);
    obj.scale.copy(scl);
    const half = Math.max(0.006, (obj.scale.y || 0.012) * 0.5);
    this.falling.push({
      mesh: obj,
      vel: new THREE.Vector3(
        (Math.random() - 0.5) * 2.2,
        0.8 + Math.random() * 1.6,
        (Math.random() - 0.5) * 2.2,
      ),
      spin: new THREE.Vector3(
        (Math.random() - 0.5) * 6,
        (Math.random() - 0.5) * 9,
        (Math.random() - 0.5) * 6,
      ),
      restY: floorY + half,
      settled: false,
    });
  }

  update(dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const c = this.live[i]!;
      c.life -= dt;
      c.vel.y -= 22 * dt;
      c.mesh.position.addScaledVector(c.vel, dt);
      c.mesh.rotation.x += c.spin.x * dt;
      c.mesh.rotation.y += c.spin.y * dt;
      c.mesh.rotation.z += c.spin.z * dt;
      if (c.mesh.position.y < c.floorY) {
        c.mesh.position.y = c.floorY;
        c.vel.y *= -0.22;
        c.vel.x *= 0.68;
        c.vel.z *= 0.68;
      }
      const mat = c.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, c.life / c.maxLife);
      if (c.life <= 0) {
        c.mesh.visible = false;
        this.live.splice(i, 1);
        this.pool.push(c);
      }
    }

    for (let i = this.falling.length - 1; i >= 0; i--) {
      const p = this.falling[i]!;
      if (p.settled) continue;
      p.vel.y -= 16 * dt;
      p.vel.x *= 1 - 1.8 * dt;
      p.vel.z *= 1 - 1.8 * dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.mesh.rotation.x += p.spin.x * dt;
      p.mesh.rotation.y += p.spin.y * dt;
      p.mesh.rotation.z += p.spin.z * dt;
      p.spin.multiplyScalar(1 - 2.4 * dt);
      if (p.mesh.position.y <= p.restY && p.vel.y <= 0) {
        p.mesh.position.y = p.restY;
        p.mesh.rotation.x *= 0.15;
        p.mesh.rotation.z *= 0.15;
        p.settled = true;
      }
    }
  }
}
